import {
  formatIsoDateAsDayMonthUpper,
  isSpecialDateStatus,
  parseIsoDateOnlyParts,
  type TimelineDisplayDates
} from 'utils/timelineDisplayDates';
import { getMonthlyTimelineDisplayDates } from './getMonthlyBadgeVariant';
import type { MonthlyTimelineItem } from './types';

function isEventLane(laneLabel: string): boolean {
  return laneLabel.toLowerCase().includes('event');
}

function isKeyDateLane(laneLabel: string): boolean {
  return laneLabel.toLowerCase().includes('key date');
}

function formatIsoDateRangeLabel(startIso: string, endIso: string): string {
  const start = parseIsoDateOnlyParts(startIso);
  const end = parseIsoDateOnlyParts(endIso);
  if (!start || !end) return '';

  const startLabel = formatIsoDateAsDayMonthUpper(startIso);
  const endLabel = formatIsoDateAsDayMonthUpper(endIso);

  if (startIso === endIso) {
    return startLabel;
  }

  if (start.year === end.year && start.month === end.month) {
    const month = startLabel.split(' ')[1];
    return `${start.day}-${end.day} ${month}`;
  }

  return `${startLabel} - ${endLabel}`;
}

/** Matches RoadmapCard Row.tsx `formatDateRange` for Key Dates. */
function formatKeyDateRangeLabel(dates?: MonthlyTimelineItem['dates']): string {
  if (!dates) return '';

  if (dates.targetStart && dates.targetEnd) {
    return formatIsoDateRangeLabel(dates.targetStart, dates.targetEnd);
  }

  if (dates.targetStart) {
    return formatIsoDateAsDayMonthUpper(dates.targetStart);
  }

  if (dates.targetMonth) {
    const startMonth = dates.targetMonth.split(' ')[0].toUpperCase();
    if (dates.targetEndMonth) {
      const endMonth = dates.targetEndMonth.split(' ')[0].toUpperCase();
      return `${startMonth} - ${endMonth}`;
    }
    return startMonth;
  }

  if (dates.targetEnd) {
    return formatIsoDateAsDayMonthUpper(dates.targetEnd);
  }

  return '';
}

/** Matches TimelineCard `renderEventDate` compact chip text for Events. */
function formatEventDateLabel(displayDates: TimelineDisplayDates): string {
  if (isSpecialDateStatus(displayDates.dateStatus)) {
    return displayDates.dateStatus!;
  }

  if (displayDates.start && displayDates.end) {
    return formatIsoDateRangeLabel(displayDates.start, displayDates.end);
  }

  if (displayDates.start) {
    return formatIsoDateAsDayMonthUpper(displayDates.start);
  }

  if (displayDates.month) {
    const month = displayDates.month.split(' ')[0].toUpperCase();
    if (displayDates.endMonth) {
      const endMonth = displayDates.endMonth.split(' ')[0].toUpperCase();
      return `${month} - ${endMonth}`;
    }
    return month;
  }

  if (displayDates.quarter) {
    return displayDates.quarter.split(' ')[0].toUpperCase();
  }

  if (displayDates.dateStatus) {
    return displayDates.dateStatus;
  }

  return 'TBD';
}

function formatDefaultLaneDateBadgeLabel(item: MonthlyTimelineItem): string {
  const displayDates = getMonthlyTimelineDisplayDates(item);

  if (displayDates.start && displayDates.end) {
    const start = parseIsoDateOnlyParts(displayDates.start);
    const end = parseIsoDateOnlyParts(displayDates.end);

    if (start && end) {
      if (start.year === end.year && start.month === end.month) {
        const month = formatIsoDateAsDayMonthUpper(displayDates.start).split(
          ' '
        )[1];
        return `${start.day}-${end.day}${month}`;
      }

      return `${formatIsoDateAsDayMonthUpper(
        displayDates.start
      )}-${formatIsoDateAsDayMonthUpper(displayDates.end)}`;
    }
  }

  if (displayDates.start) {
    return formatIsoDateAsDayMonthUpper(displayDates.start);
  }

  if (displayDates.month) {
    return displayDates.month.split(' ')[0].toUpperCase();
  }

  if (displayDates.quarter) {
    return displayDates.quarter.split(' ')[0].toUpperCase();
  }

  return 'TBD';
}

export function formatMonthlyDateBadgeLabel(
  item: MonthlyTimelineItem,
  laneLabel: string
): string {
  if (isKeyDateLane(laneLabel)) {
    const label = formatKeyDateRangeLabel(item.dates);
    return label || 'TBD';
  }

  if (isEventLane(laneLabel)) {
    return formatEventDateLabel(getMonthlyTimelineDisplayDates(item));
  }

  return formatDefaultLaneDateBadgeLabel(item);
}
